import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormLabel } from '../../../../components/UI-CHAKRA/FormLabel';
import { Spinner } from '../../../../components/UI-CHAKRA/Spinner';
import { Flex } from '../../../../components/UI-CHAKRA/Flex';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Box } from '../../../../components/UI-CHAKRA/Box';
import { Input } from '../../../../components/UI-CHAKRA/Input/Input';
import { Text } from '../../../../components/UI-CHAKRA/Text';
import { SelectOption } from '../../../../interfaces/formula';
import { logger } from '../../../../helpers/logger';
import { debounce } from 'lodash';

interface LazyLoadingSelectProps {
  value: string | number;
  onChange(val: string | number): void;
  loadOptions: (page: number, pageSize: number, query?: string) => Promise<{ items: SelectOption[]; count: number }>;
  label?: string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  onBlur?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
  'data-testid'?: string;
  pageSize?: number;
  enableLocalFilter?: boolean;
}

function LazyLoadingSelect(props: LazyLoadingSelectProps) {
  const {
    value,
    onChange,
    loadOptions,
    label,
    placeholder = 'Selecione uma opção',
    error,
    disabled,
    required,
    onBlur,
    onOpen,
    onClose,
    'data-testid': dataTestId,
    pageSize = 20,
    enableLocalFilter = false,
  } = props;

  const [options, setOptions] = useState<SelectOption[]>([]);
  const [allOptions, setAllOptions] = useState<SelectOption[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

  const loadInitialOptions = useCallback(
    async (query?: string) => {
      if (loading) return;

      setLoading(true);
      try {
        if (enableLocalFilter) {
          let allItems: SelectOption[] = [];
          let page = 1;
          let hasMorePages = true;

          while (hasMorePages) {
            const data = await loadOptions(page, pageSize);
            allItems = [...allItems, ...data.items];
            const totalPages = Math.ceil(data.count / pageSize);
            hasMorePages = page < totalPages;
            page++;
          }

          setAllOptions(allItems);
          setOptions(allItems);
          setHasMore(false);
          setCurrentPage(1);
        } else {
          const data = await loadOptions(1, pageSize, query);
          setOptions(data.items);
          const totalPages = Math.ceil(data.count / pageSize);
          setHasMore(1 < totalPages);
          setCurrentPage(1);
        }
        setHasLoadedInitial(true);
      } catch (error) {
        logger.error('Erro ao carregar opções:', error);
      } finally {
        setLoading(false);
      }
    },
    [loadOptions, pageSize, loading, enableLocalFilter]
  );

  const loadMoreOptions = useCallback(async () => {
    if (loadingMore || !hasMore || enableLocalFilter) return;

    setLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const data = await loadOptions(nextPage, pageSize, searchQuery);
      setOptions((prev) => [...prev, ...data.items]);
      const totalPages = Math.ceil(data.count / pageSize);
      setHasMore(nextPage < totalPages);
      setCurrentPage(nextPage);
    } catch (error) {
      logger.error('Erro ao carregar mais opções:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadOptions, pageSize, currentPage, hasMore, loadingMore, searchQuery, enableLocalFilter]);

  const debouncedSearch = useMemo(
    () =>
      debounce((query: string) => {
        if (enableLocalFilter) {
          if (query.trim() === '') {
            setOptions(allOptions);
          } else {
            const filtered = allOptions.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));
            setOptions(filtered);
          }
        } else {
          setOptions([]);
          setCurrentPage(1);
          setHasLoadedInitial(false);
          loadInitialOptions(query || undefined);
        }
      }, 300),
    [loadInitialOptions, enableLocalFilter, allOptions]
  );

  useEffect(() => {
    if (isOpen) {
      if (enableLocalFilter) {
        if (allOptions.length === 0 && !hasLoadedInitial) {
          loadInitialOptions();
        } else if (searchQuery.trim() === '') {
          setOptions(allOptions);
        }
      } else if (options.length === 0 && !searchQuery && !hasLoadedInitial) {
        loadInitialOptions();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options.length, searchQuery, loadInitialOptions, enableLocalFilter, allOptions.length, hasLoadedInitial]);

  useEffect(() => {
    if (value && !loading && !isOpen && !searchQuery) {
      if (enableLocalFilter) {
        if (allOptions.length === 0 && !hasLoadedInitial) {
          loadInitialOptions();
        } else {
          setOptions(allOptions);
          const selectedOption = allOptions.find((option) => option.value === value);
          if (selectedOption && inputValue !== selectedOption.label) {
            setInputValue(selectedOption.label);
          }
        }
      } else {
        const valueExists = options.find((option) => option.value === value);
        if ((options.length === 0 || !valueExists) && !hasLoadedInitial) {
          loadInitialOptions();
        } else if (valueExists && inputValue !== valueExists.label) {
          setInputValue(valueExists.label);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    value,
    options.length,
    loading,
    isOpen,
    searchQuery,
    loadInitialOptions,
    enableLocalFilter,
    allOptions.length,
    inputValue,
    hasLoadedInitial,
  ]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setInputValue(query);
    setSearchQuery(query);
    setIsOpen(true);

    if (enableLocalFilter) {
      if (query.trim() === '') {
        setOptions(allOptions);
      } else {
        const filtered = allOptions.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));
        setOptions(filtered);
      }
    } else {
      debouncedSearch(query);
    }
  };

  const handleSelectOption = useCallback(
    (val: string | number) => {
      onChange(val);
      setIsOpen(false);
      setSearchQuery('');
      const selectedOption =
        options.find((option) => option.value === val) || allOptions.find((option) => option.value === val);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      }
    },
    [onChange, options, allOptions]
  );

  const menuItems = useMemo(() => {
    return options.map((option, index) => (
      <Box
        key={`${index}-${option.value}`}
        onClick={() => handleSelectOption(option.value)}
        fontSize={'14px'}
        fontFamily={'Ambit-Regular, Inter, sans-serif'}
        px={3}
        py={2}
        cursor={'pointer'}
        _hover={{
          background: 'gray.lightest',
        }}
        whiteSpace={'break-spaces'}
        data-value={option.value}
      >
        <Text fontSize={'14px'}>{option.label}</Text>
      </Box>
    ));
  }, [options, handleSelectOption]);

  const displayLabel = useMemo(() => {
    if (!label) return null;
    return `${label}${required ? '*' : ''}`;
  }, [label, required]);

  useEffect(() => {
    if (value && !isOpen) {
      const selectedOption =
        options.find((option) => option.value === value) || allOptions.find((option) => option.value === value);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else {
        setInputValue(String(value));
      }
    } else if (!value && !isOpen) {
      setInputValue('');
      setHasLoadedInitial(false);
    }
  }, [value, isOpen, options, allOptions]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    if (value) {
      const selectedOption =
        options.find((option) => option.value === value) || allOptions.find((option) => option.value === value);
      if (selectedOption) {
        setInputValue(selectedOption.label);
      } else {
        setInputValue(String(value));
      }
      if (enableLocalFilter) {
        setOptions(allOptions);
      } else if (!hasLoadedInitial) {
        loadInitialOptions();
      }
    } else {
      setInputValue('');
      if (!enableLocalFilter) {
        setOptions([]);
        setCurrentPage(1);
        setHasLoadedInitial(false);
      } else {
        setOptions(allOptions);
      }
    }
    if (onClose) {
      onClose();
    }
  }, [value, options, allOptions, enableLocalFilter, loadInitialOptions, onClose, hasLoadedInitial]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('[data-autocomplete-container]')) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, handleClose]);

  return (
    <>
      {displayLabel && <FormLabel>{displayLabel}</FormLabel>}
      <Box position={'relative'} data-autocomplete-container>
        <Box position={'relative'}>
          <Input
            width={'100%'}
            border={'1px solid'}
            padding={'8px 40px 8px 12px'}
            h={'40px'}
            borderRadius={'10px'}
            isDisabled={disabled}
            bg={'#fff'}
            _focus={{
              borderColor: '#3182CE',
            }}
            borderColor={error ? 'error.main' : '#CBBFE6'}
            borderWidth={error ? '2px' : '1px'}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              setIsOpen(true);
              if (!value) {
                setInputValue('');
              }
              if (onOpen) {
                onOpen();
              }
            }}
            onBlur={(e) => {
              setTimeout(() => {
                if (!isOpen) {
                  if (value) {
                    const selectedOption =
                      options.find((option) => option.value === value) ||
                      allOptions.find((option) => option.value === value);
                    if (selectedOption) {
                      setInputValue(selectedOption.label);
                    } else {
                      setInputValue(String(value));
                    }
                  } else {
                    setInputValue('');
                  }
                  if (onBlur) {
                    onBlur();
                  }
                }
              }, 200);
            }}
            placeholder={placeholder}
            _disabled={{
              opacity: '0.4',
              cursor: 'not-allowed',
            }}
            data-testid={dataTestId}
          />
          <Box position={'absolute'} right={'12px'} top={'50%'} transform={'translateY(-50%)'} pointerEvents={'none'}>
            <ChevronDownIcon />
          </Box>
        </Box>
        {isOpen && (
          <Box
            border={'1px'}
            borderColor={'#CBBFE6'}
            borderRadius={'10px'}
            maxHeight={'200px'}
            overflow={'hidden'}
            position={'absolute'}
            left={0}
            top={'100%'}
            mt={1}
            maxW={'100%'}
            w={'100%'}
            p={0}
            zIndex={1000}
            bg={'#fff'}
            boxShadow={'0px 4px 6px rgba(0, 0, 0, 0.1)'}
          >
            <Box
              maxHeight={'200px'}
              overflowY={'auto'}
              overflowX={'hidden'}
              borderBottomRadius={'10px'}
              onScroll={(e) => {
                if (enableLocalFilter) return;

                const target = e.target as HTMLElement;
                const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

                if (scrollBottom < 50 && hasMore && !loadingMore) {
                  loadMoreOptions();
                }
              }}
            >
              <Box pb={1}>
                {loading && options.length === 0 ? (
                  <Flex justifyContent={'center'} p={4}>
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <>
                    {menuItems}
                    {loadingMore && (
                      <Flex justifyContent={'center'} p={2}>
                        <Spinner size="sm" />
                      </Flex>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}

export default React.memo(LazyLoadingSelect);
